import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Trophy, Calendar, Clock, Info, AlertTriangle, CheckCircle, ArrowRight, Utensils, ChevronRight } from 'lucide-react';
import API_URL from '../../config';
import SEO from './SEO';

const BackgroundPattern = () => (
    <svg viewBox="0 0 1440 800" className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none" preserveAspectRatio="xMidYMid slice">
        <defs>
            <pattern id="wave" x="0" y="0" width="120" height="80" patternUnits="userSpaceOnUse">
                <path d="M0 40 Q 30 10, 60 40 T 120 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="4" />
                <path d="M0 80 Q 30 50, 60 80 T 120 80" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="4" />
            </pattern>
            <linearGradient id="blueGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1e3a8a" />
                <stop offset="100%" stopColor="#1e40af" />
            </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#blueGrad)" />
        <rect width="100%" height="100%" fill="url(#wave)" />
        <path d="M 0,200 C 400,100 800,400 1440,200" fill="none" stroke="#06b6d4" strokeOpacity="0.1" strokeWidth="40" strokeLinecap="round" />
    </svg>
);

export default function PageDomov() {
    const navigate = useNavigate();
    const [config, setConfig] = useState({});
    const [harmonogram, setHarmonogram] = useState([]);
    const [records, setRecords] = useState([]);

    useEffect(() => {
        fetch(`${API_URL}/web-config`)
            .then(r => r.json())
            .then(setConfig)
            .catch(() => { });
        fetch(`${API_URL}/harmonogram`)
            .then(r => r.json())
            .then(setHarmonogram)
            .catch(() => { });
        fetch(`${API_URL}/records`)
            .then(r => r.json())
            .then(setRecords)
            .catch(() => { });
    }, []);

    const rocnik = config.rocnik || '---';
    const datum = config.datum || 'Nepodarilo sa načítať dátum';
    const introText1 = config.intro_text_1 || 'Nepodarilo sa načítať informácie o podujatí.';
    const introText2 = config.intro_text_2 || 'Nepodarilo sa načítať informácie o podujatí.';
    const kmTrasa = config.km_trasa || '-';
    const pocetKategorii = config.pocet_kategorii || '-';

    const defaultHarmonogram = [
        { cas: 'TERAZ', text: 'Nepodarilo sa načítať harmonogram', lokalita: 'Nepodarilo sa načítať harmonogram' }
    ];

    const harmonogramItems = harmonogram.length > 0 ? harmonogram : defaultHarmonogram;

    return (
        <div className="relative w-full pb-20 overflow-x-hidden">
            <SEO
                title="Malodunajský vodácky maratón | Oficiálna stránka | KMD"
                description="Najväčšie vodácke podujatie na Malom Dunaji. Pridajte sa k nám na tradičné preteky. Vodácky maratón pre profíkov aj rodiny."
                keywords="vodácky maratón, Malý Dunaj, splav, kanoe, kmd, vodácke preteky"
            />
            {/* Hero */}
            <div className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
                <BackgroundPattern />
                <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center pt-32 pb-24">
                    <div className="flex flex-col items-start lg:col-span-7">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-[4px] border-yellow-400 p-1 shadow-[0_0_30px_rgba(255,255,255,0.2)] bg-white shrink-0">
                                <img src="https://klubmalydunaj.sk/wp-content/uploads/2020/02/logo-3.png" alt="KMD Logo" className="w-full h-full object-contain rounded-full" />
                            </div>
                            <div className="flex flex-col items-start gap-2">
                                <div className="bg-yellow-400 px-4 py-1.5 rounded-sm shadow-md transform -skew-x-12">
                                    <span className="block text-xl md:text-2xl lg:text-3xl font-black text-blue-900 uppercase tracking-widest leading-none drop-shadow-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {config.rocnik || '---'} Ročník
                                    </span>
                                </div>
                                <div className="bg-cyan-400 px-4 py-1.5 rounded-sm shadow-md flex items-center gap-2 transform -skew-x-12">
                                    <Calendar className="w-4 h-4 text-blue-900 transform skew-x-12" />
                                    <span className="text-blue-900 font-black tracking-widest block transform skew-x-12 text-xs md:text-sm lg:text-base uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {datum}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <h1 className="text-white font-black leading-[0.9] uppercase tracking-wider drop-shadow-2xl text-3xl sm:text-4xl md:text-5xl lg:text-[4.5rem] xl:text-[5rem] mb-8 w-full" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Malodunajský<br />Vodácky<br />
                            <span className="text-yellow-400 drop-shadow-[0_0_20px_rgba(250,204,21,0.5)]">Maratón</span>
                        </h1>

                        <p className="text-white/90 text-lg md:text-2xl font-bold mb-10 max-w-xl border-l-4 border-cyan-400 pl-4">
                            Najväčšie vodácke podujatie na Malom Dunaji pre profesionálov aj rodiny s deťmi!
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <button onClick={() => navigate('/registracia')} className="font-black uppercase tracking-widest px-6 py-3 md:px-8 md:py-4 shadow-xl transition-all transform -skew-x-12 hover:scale-105 inline-flex items-center gap-2 bg-yellow-400 text-blue-900 hover:bg-yellow-300 text-sm md:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                <span className="transform skew-x-12 flex items-center gap-2">Registrácia <ArrowRight className="w-5 h-5" /></span>
                            </button>
                            <button onClick={() => navigate('/pravidla')} className="font-black uppercase tracking-widest px-6 py-3 md:px-8 md:py-4 shadow-xl transition-all transform -skew-x-12 hover:scale-105 inline-flex items-center gap-2 bg-white/10 text-white border-2 border-white/20 hover:bg-white/20 backdrop-blur-sm text-sm md:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                <span className="transform skew-x-12">Pravidlá a Trasy</span>
                            </button>
                        </div>
                    </div>

                    {/* Štatistiky */}
                    <div className="grid grid-cols-2 gap-3 md:gap-4 w-full max-w-md mx-auto lg:mx-0 lg:ml-auto lg:col-span-5 mt-8 lg:mt-0">
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 md:p-6 rounded-2xl flex flex-col items-center justify-center text-center shadow-2xl">
                            <MapPin className="w-8 h-8 md:w-12 md:h-12 text-yellow-400 mb-2" />
                            <span className="text-2xl md:text-4xl font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>{kmTrasa}</span>
                            <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px] md:text-xs mt-1">Hlavná trasa</span>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 md:p-6 rounded-2xl flex flex-col items-center justify-center text-center shadow-2xl">
                            <Trophy className="w-8 h-8 md:w-12 md:h-12 text-yellow-400 mb-2" />
                            <span className="text-2xl md:text-4xl font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>{pocetKategorii}</span>
                            <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px] md:text-xs mt-1">Kategórií</span>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl flex flex-col items-center justify-center text-center shadow-2xl col-span-2">
                            <Utensils className="w-8 h-8 md:w-10 md:h-10 text-orange-400 mb-2" />
                            <span className="text-lg md:text-xl font-black text-white uppercase tracking-wider mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Najlepšia Vodácka Buchta</span>
                            <p className="text-white/80 text-xs md:text-sm font-medium mb-3">Sprievodná súťaž v pečení pre divákov aj vodákov.</p>
                            <button
                                onClick={() => navigate('/buchta')}
                                className="font-black uppercase tracking-widest px-4 py-2 mt-2 shadow-xl transition-all transform -skew-x-12 hover:scale-105 inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 text-[10px] md:text-xs"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                <span className="transform skew-x-12 flex items-center gap-1">Zistiť viac <ChevronRight className="w-3.5 h-3.5" /></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Traťové rekordy */}
            {records.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-16 md:mt-20">
                    <div className="flex flex-col items-center mb-10 text-center">
                        <Trophy className="w-10 h-10 md:w-12 md:h-12 text-yellow-400 mb-4 drop-shadow-md" />
                        <h3 className="text-2xl md:text-5xl font-black text-white uppercase tracking-normal sm:tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Traťové Rekordy
                        </h3>
                        <div className="w-16 md:w-24 h-1 md:h-1.5 bg-cyan-400 mt-4 md:mt-6 rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {records.sort((a, b) => a.order - b.order).map((rec, idx) => (
                            <div key={idx} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 md:p-6 shadow-2xl relative overflow-hidden group hover:bg-white/15 transition-colors">
                                <div className="absolute top-0 right-0 bg-yellow-400 text-blue-900 font-black text-[10px] md:text-xs px-3 py-1 rounded-bl-xl uppercase tracking-widest">{rec.year}</div>
                                <h4 className="text-cyan-400 font-black text-base md:text-lg uppercase tracking-wider mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>{rec.category}</h4>
                                <div className="text-2xl md:text-3xl font-black text-white mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>{rec.time}</div>
                                <div className="text-white/80 font-bold text-sm leading-tight flex items-start gap-2">
                                    <div className="w-6 h-6 rounded-full bg-blue-900 flex items-center justify-center shrink-0 border border-white/20 mt-0.5">
                                        <Trophy className="w-3.5 h-3.5 text-yellow-400" />
                                    </div>
                                    <span className="break-words">{rec.names}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* O Maratóne + Harmonogram */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12">
                <div className="space-y-8">
                    <div>
                        <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-widest mb-4 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            <Info className="text-cyan-400 w-6 h-6 md:w-8 md:h-8" /> O Podujatí
                        </h3>
                        <p className="text-white/80 font-medium text-base md:text-lg leading-relaxed mb-4">{introText1}</p>
                        <p className="text-white/80 font-medium text-base md:text-lg leading-relaxed">{introText2}</p>
                    </div>
                    <div className="bg-yellow-400 rounded-2xl p-6 md:p-8 shadow-xl">
                        <h3 className="text-blue-900 font-black text-xl md:text-2xl uppercase tracking-widest mb-4 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            <CheckCircle className="text-blue-900 w-6 h-6 md:w-8 md:h-8" /> Registrácia
                        </h3>
                        <p className="text-blue-900/90 font-bold text-base md:text-lg mb-4">
                            Registrovať sa môžete pomocou online registračného formulára na našom webe, vždy <strong className="text-blue-900 bg-white/50 px-2 py-0.5 rounded">od 1. apríla</strong> daného roku.
                        </p>
                        <div className="bg-white/30 border border-blue-900/20 p-4 rounded-xl">
                            <p className="text-blue-900 font-bold text-sm md:text-base flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                                <span>Registrácia na mieste v deň štartu je možná <strong>iba po predchádzajcej dohode</strong> s organizátormi!</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Harmonogram */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-8 backdrop-blur-sm">
                    <h3 className="text-white font-black text-2xl md:text-3xl uppercase tracking-widest mb-8 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        <Clock className="text-cyan-400 w-6 h-6 md:w-8 md:h-8" /> Harmonogram
                    </h3>
                    <div className="relative pt-4 pb-4">
                        {/* Vertikálna čiara */}
                        <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-white/20 to-transparent -translate-x-1/2 rounded-full"></div>

                        <div className="space-y-6 md:space-y-8 relative">
                            {harmonogramItems.map((item, i) => (
                                <div key={i} className="relative flex w-full">
                                    {/* Krúžok s bodkou */}
                                    <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full border-4 border-blue-950 bg-cyan-400 z-10 shadow-md flex items-center justify-center top-3 md:top-1/2 md:-translate-y-1/2">
                                        <div className="w-2 md:w-2.5 h-2 md:h-2.5 bg-blue-950 rounded-full"></div>
                                    </div>

                                    {/* Karta */}
                                    <div className={`w-full md:w-[calc(50%-2.5rem)] pl-14 md:pl-0 ${i % 2 === 0 ? 'md:pr-0 md:mr-auto md:text-right' : 'md:ml-auto md:pl-0 md:text-left'}`}>
                                        <div className="bg-blue-900/50 p-4 rounded-xl border border-white/10 shadow-lg hover:bg-blue-800/60 transition-colors">
                                            <div className={`flex flex-col md:flex-row flex-wrap items-start gap-2 mb-2 ${i % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                                                <span className="text-yellow-400 font-black text-base md:text-lg block" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.cas}</span>
                                                <span className="inline-flex items-center gap-1 bg-white/10 text-cyan-300 text-[10px] md:text-[0.65rem] font-bold px-2 py-1 rounded uppercase tracking-wider">
                                                    <MapPin className="w-3 h-3" /> {item.lokalita}
                                                </span>
                                            </div>
                                            <p className="text-white/90 font-medium text-xs md:text-sm leading-relaxed">{item.text}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
